import { useState } from "react";
import { api, formatCurrency } from "../lib/api";
import Layout from "../components/Layout";
import { Calculator } from "lucide-react";
import { Field } from "./Properties";

const DEFAULTS = {
  nightly_rate: 225,
  occupancy_pct: 65,
  cleaning_fee: 95,
  platform_fee_pct: 15,
  monthly_rent: 2400,
  monthly_expenses: 350,
};

export default function Strategy() {
  const [form, setForm] = useState(DEFAULTS);
  const [result, setResult] = useState(null);

  const compute = async (e) => {
    e?.preventDefault();
    const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, parseFloat(v) || 0]));
    const r = await api.post("/strategy/compare", payload);
    setResult(r.data);
  };

  const winner = result ? (result.str_annual_net > result.ltr_annual_net ? "STR" : "LTR") : null;

  return (
    <Layout title="Strategy Switcher" subtitle="Compare STR vs LTR projected returns">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={compute} className="card p-6 space-y-4" data-testid="strategy-form">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-full bg-[#1C3A5E]/10 text-[#1C3A5E] flex items-center justify-center">
              <Calculator size={18} />
            </div>
            <h3 className="font-bold text-lg">Inputs</h3>
          </div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#86868B] font-semibold pt-2">Short-Term</div>
          <Field label="Nightly Rate ($)" type="number" value={form.nightly_rate} onChange={(v) => setForm({...form, nightly_rate: v})} testId="strat-nightly" />
          <Field label="Occupancy (%)" type="number" value={form.occupancy_pct} onChange={(v) => setForm({...form, occupancy_pct: v})} testId="strat-occupancy" />
          <Field label="Cleaning Fee per Stay ($)" type="number" value={form.cleaning_fee} onChange={(v) => setForm({...form, cleaning_fee: v})} testId="strat-cleaning" />
          <Field label="Platform Fee (%)" type="number" value={form.platform_fee_pct} onChange={(v) => setForm({...form, platform_fee_pct: v})} testId="strat-platform" />
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#86868B] font-semibold pt-2">Long-Term</div>
          <Field label="Monthly Rent ($)" type="number" value={form.monthly_rent} onChange={(v) => setForm({...form, monthly_rent: v})} testId="strat-rent" />
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#86868B] font-semibold pt-2">Shared</div>
          <Field label="Monthly Operating Expenses ($)" type="number" value={form.monthly_expenses} onChange={(v) => setForm({...form, monthly_expenses: v})} testId="strat-expenses" />
          <button type="submit" className="btn-primary w-full" data-testid="strat-compute">Compare</button>
        </form>

        <div className="space-y-4">
          {result && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <ScenarioCard title="Short-Term Rental" net={result.str_annual_net} revenue={result.str_annual_revenue} expenses={result.str_annual_expenses} winner={winner === "STR"} testId="card-str" />
                <ScenarioCard title="Long-Term Rental" net={result.ltr_annual_net} revenue={result.ltr_annual_revenue} expenses={result.ltr_annual_expenses} winner={winner === "LTR"} testId="card-ltr" />
              </div>
              <div className="card p-6 bg-[#1C3A5E] text-white" data-testid="strat-recommendation">
                <div className="text-[11px] uppercase tracking-[0.22em] opacity-70 font-semibold">Recommendation</div>
                <p className="text-base font-semibold mt-2 leading-relaxed">{result.recommendation}</p>
              </div>
            </>
          )}
          {!result && (
            <div className="card p-8 text-center text-[#86868B] text-sm">
              Adjust your inputs and tap "Compare" to see projections.
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function ScenarioCard({ title, net, revenue, expenses, winner, testId }) {
  return (
    <div className={`card p-6 ${winner ? "ring-2 ring-[#1C3A5E]" : ""}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#86868B] font-semibold">{title}</div>
        {winner && <span className="tag tag-success">Winner</span>}
      </div>
      <div className="text-3xl font-extrabold tracking-tighter mt-2">{formatCurrency(net)}</div>
      <div className="text-xs text-[#86868B]">Projected annual net</div>
      <div className="mt-4 text-sm space-y-1">
        <div className="flex justify-between"><span className="text-[#86868B]">Revenue</span><span className="font-semibold">{formatCurrency(revenue)}</span></div>
        <div className="flex justify-between"><span className="text-[#86868B]">Expenses</span><span className="font-semibold">{formatCurrency(expenses)}</span></div>
      </div>
    </div>
  );
}
